import type { RealtimeChannel } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { useAuth } from '../../app/AuthProvider';
import { supabase } from '../../lib/supabase';
import { tapHaptic } from '../../shared/lib/haptics';
import { useToast } from '../../shared/ui/toast-context';

import {
  getUnreadCount,
  listNotifications,
  markAllRead,
  markRead,
  NOTIFICATION_QUERY_KEYS,
  type NotificationRow,
} from './notifications-api';

// Realtime needs `public.notifications` in the supabase_realtime publication
// (the Phase 127 migration adds it, but the statement is deliberately
// non-fatal). Poll as well so the badge is still correct when it isn't there.
const POLL_MS = 60_000;

interface ChannelEntry {
  channel: RealtimeChannel;
  listeners: Set<() => void>;
}

/**
 * One realtime channel per user, however many components call the hook.
 *
 * `/student/notifications` and `/institute/notifications` mount TWO consumers
 * at once — the page itself plus the sidebar row's unread count, which is in
 * the shell on every route. Both
 * used to call `supabase.channel('notifications-<uid>')`, and supabase-js
 * returns the channel it already holds for a topic rather than a new one, so
 * the second consumer's `.on('postgres_changes', …)` landed on a channel that
 * had already subscribed and threw "cannot add `postgres_changes` callbacks …
 * after `subscribe()`" — during render, which took the whole route down to the
 * error boundary.
 *
 * Ref-counting here keeps a single subscription and one INSERT -> one
 * invalidate, and the unique topic suffix means a remount never races the
 * still-unsubscribing channel it is replacing (supabase-js drops a removed
 * channel from its registry asynchronously).
 */
const channels = new Map<string, ChannelEntry>();
let topicSeq = 0;

function subscribeToNotifications(userId: string, onChange: () => void): () => void {
  let entry = channels.get(userId);
  if (entry === undefined) {
    const listeners = new Set<() => void>();
    topicSeq += 1;
    const channel = supabase
      .channel(`notifications-${userId}-${String(topicSeq)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          for (const listener of listeners) listener();
        },
      )
      .subscribe();
    entry = { channel, listeners };
    channels.set(userId, entry);
  }
  const active = entry;
  active.listeners.add(onChange);
  return () => {
    active.listeners.delete(onChange);
    if (active.listeners.size > 0) return;
    // Only drop the map entry if it is still ours: a StrictMode remount can
    // have installed a replacement before this cleanup runs.
    if (channels.get(userId) === active) channels.delete(userId);
    void supabase.removeChannel(active.channel);
  };
}

/**
 * Ref-counted realtime + the query invalidation it drives. Extracted so the
 * sidebar's count and the full page share one subscription — they are mounted
 * together on /student/notifications, and a second module-level channel map
 * would resurrect the "cannot add `postgres_changes` callbacks after
 * `subscribe()`" crash the comment above describes.
 */
function useNotificationsRealtime(userId: string | null): void {
  const queryClient = useQueryClient();

  const invalidate = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [queryClient]);

  useEffect(() => {
    if (userId === null) return undefined;
    return subscribeToNotifications(userId, invalidate);
  }, [userId, invalidate]);
}

/**
 * Just the unread count, for the sidebar's Notifications row.
 *
 * `enabled` is false in a workspace with no notification feed (admin), where
 * fetching a count nothing renders would cost a query and a realtime channel.
 */
export function useUnreadNotificationCount(enabled: boolean): number {
  const { user } = useAuth();
  const userId = enabled ? (user?.id ?? null) : null;

  const unread = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.unreadCount(userId ?? ''),
    queryFn: () => getUnreadCount(userId as string),
    enabled: userId !== null,
    staleTime: 30_000,
    refetchInterval: POLL_MS,
  });

  useNotificationsRealtime(userId);

  return unread.data ?? 0;
}

export interface NotificationsState {
  list: UseQueryResult<NotificationRow[]>;
  unreadCount: number;
  markOneRead: (id: number) => void;
  markEveryRead: () => void;
  isMarkingAll: boolean;
}

export function useNotifications(limit: number): NotificationsState {
  const queryClient = useQueryClient();
  const { show } = useToast();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const list = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.list(userId ?? '', limit),
    queryFn: () => listNotifications(userId as string, limit),
    enabled: userId !== null,
    staleTime: 30_000,
    refetchInterval: POLL_MS,
  });

  const unread = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.unreadCount(userId ?? ''),
    queryFn: () => getUnreadCount(userId as string),
    enabled: userId !== null,
    staleTime: 30_000,
    refetchInterval: POLL_MS,
  });

  const invalidate = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [queryClient]);

  useNotificationsRealtime(userId);

  const readOne = useMutation({ mutationFn: markRead, onSuccess: invalidate });
  // "Mark all read" is a deliberate press whose only other visible effect is
  // off-screen (the sidebar count goes), so it gets one tick
  // and one line of confirmation. Marking a single row read is the row itself
  // changing — no toast for that.
  const readAll = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      invalidate();
      tapHaptic();
      show('All notifications marked as read', { tone: 'success' });
    },
  });

  const markOneRead = useCallback(
    (id: number): void => {
      readOne.mutate(id);
    },
    [readOne],
  );
  const markEveryRead = useCallback((): void => {
    readAll.mutate();
  }, [readAll]);

  return {
    list,
    unreadCount: unread.data ?? 0,
    markOneRead,
    markEveryRead,
    isMarkingAll: readAll.isPending,
  };
}
